import { useState } from 'react';
import { Supplier, SupplierCategory, SupplierStatus } from '../../../mocks/suppliers';

interface AddSupplierModalProps {
  initialData?: Supplier | null;
  onClose: () => void;
  onSave: (data: Partial<Supplier>) => void;
}

const CATEGORIES: SupplierCategory[] = [
  'Hotel & Resort', 'Airline', 'DMC & Tour Operator', 'Visa Service',
  'Transfer & Transport', 'Cruise Line', 'Safari & Adventure',
];

const STATUSES: SupplierStatus[] = ['Active', 'Preferred', 'Inactive', 'Blacklisted'];

const LOGO_COLORS = ['#0D9488', '#D97706', '#7C3AED', '#059669', '#1D4ED8', '#BE185D', '#92400E', '#DC2626', '#0284C7', '#6D28D9'];

export default function AddSupplierModal({ initialData, onClose, onSave }: AddSupplierModalProps) {
  const [name, setName] = useState(initialData?.name ?? '');
  const [category, setCategory] = useState<SupplierCategory>(initialData?.category ?? 'Hotel & Resort');
  const [status, setStatus] = useState<SupplierStatus>(initialData?.status ?? 'Active');
  const [country, setCountry] = useState(initialData?.country ?? '');
  const [city, setCity] = useState(initialData?.city ?? '');
  const [website, setWebsite] = useState(initialData?.website ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [logoColor, setLogoColor] = useState(initialData?.logoColor ?? LOGO_COLORS[0]);
  const [paymentTerms, setPaymentTerms] = useState(initialData?.paymentTerms ?? 'Net 30 days');
  const [currency, setCurrency] = useState(initialData?.currency ?? 'AED');
  const [contractExpiry, setContractExpiry] = useState(initialData?.contractExpiry ?? '2026-12-31');
  const [notes, setNotes] = useState(initialData?.notes ?? '');
  const [contactName, setContactName] = useState(initialData?.contacts?.[0]?.name ?? '');
  const [contactRole, setContactRole] = useState(initialData?.contacts?.[0]?.role ?? '');
  const [contactEmail, setContactEmail] = useState(initialData?.contacts?.[0]?.email ?? '');
  const [contactPhone, setContactPhone] = useState(initialData?.contacts?.[0]?.phone ?? '');
  const [contactWhatsapp, setContactWhatsapp] = useState(initialData?.contacts?.[0]?.whatsapp ?? '');

  const logoInitials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '??';

  const handleSave = () => {
    onSave({
      name,
      category,
      status,
      country,
      city,
      website,
      description,
      logo: logoInitials,
      logoColor,
      paymentTerms,
      currency,
      contractExpiry,
      notes,
      contacts: [{ name: contactName, role: contactRole, email: contactEmail, phone: contactPhone, whatsapp: contactWhatsapp || undefined }],
      rating: initialData?.rating ?? 4.0,
      reviewCount: initialData?.reviewCount ?? 0,
      tags: initialData?.tags ?? [],
      rates: initialData?.rates ?? [],
      bookings: initialData?.bookings ?? [],
      totalBookings: initialData?.totalBookings ?? 0,
      totalCostValue: initialData?.totalCostValue ?? 0,
      totalSellValue: initialData?.totalSellValue ?? 0,
      lastUsed: initialData?.lastUsed ?? new Date().toISOString().slice(0, 10),
      createdAt: initialData?.createdAt ?? new Date().toISOString().slice(0, 10),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <i className={`${initialData ? 'ri-edit-line' : 'ri-add-box-line'} text-base`} />
            </div>
            <p className="text-sm font-semibold text-gray-800">
              {initialData ? `Edit Supplier — ${initialData.name}` : 'Add New Supplier'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Logo Preview */}
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-base flex-shrink-0"
              style={{ backgroundColor: logoColor }}
            >
              {logoInitials}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">Logo Color</p>
              <div className="flex gap-1.5 flex-wrap">
                {LOGO_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setLogoColor(c)}
                    className={`w-7 h-7 rounded-lg cursor-pointer transition-all ${logoColor === c ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Supplier Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Soneva Fushi Resort"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Category *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as SupplierCategory)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as SupplierStatus)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Country *</label>
              <input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="e.g. Maldives"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">City</label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Baa Atoll"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Website</label>
              <input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="www.example.com"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Contract Expiry</label>
              <input
                type="date"
                value={contractExpiry}
                onChange={(e) => setContractExpiry(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Brief description of the supplier..."
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 resize-none"
            />
          </div>

          {/* Financial */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Payment Terms</label>
              <input
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="e.g. Net 30 days"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Invoice Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 bg-white cursor-pointer"
              >
                {['AED', 'USD', 'EUR', 'GBP', 'JPY', 'SGD', 'CHF', 'EGP', 'THB', 'IDR'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Primary Contact */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Primary Contact</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Full Name</label>
                <input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Contact name"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Role / Title</label>
                <input
                  value={contactRole}
                  onChange={(e) => setContactRole(e.target.value)}
                  placeholder="e.g. Reservations Manager"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Email</label>
                <input
                  type="email"
                  name="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="contact@supplier.com"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Phone</label>
                <input
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="+971 4 123 4567"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">WhatsApp (optional)</label>
                <input
                  value={contactWhatsapp}
                  onChange={(e) => setContactWhatsapp(e.target.value)}
                  placeholder="+971 50 123 4567"
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Internal Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Commission rates, special arrangements, important notes..."
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-amber-400 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name || !country}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-save-line" /> {initialData ? 'Save Changes' : 'Add Supplier'}
          </button>
        </div>
      </div>
    </div>
  );
}
